import React, { useEffect, useState, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  PawPrint, Calendar, Send, Bot, Plus, MapPin, Sparkles, CheckCircle2, Clock,
} from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CHIPPY_AVATAR = "https://customer-assets.emergentagent.com/job_pawsitive-app-1/artifacts/ht2dmhe2_ChatGPT%20Image%20Apr%2025%2C%202026%2C%2005_16_02%20PM.png";
const TEMPERAMENTS = ["playful", "shy", "reactive", "anxious", "calm", "high-energy"];
const SERVICE_OPTS = ["one_on_one", "board_train", "group", "behavioral", "puppy", "reactivity", "pack_walks"];

export default function ParentDashboard() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [tasks, setTasks] = useState([]);

  // Dog profile dialog + add-dog dialog
  const [dogDetail, setDogDetail] = useState(null);
  const [addDogOpen, setAddDogOpen] = useState(false);
  const [newDog, setNewDog] = useState({
    name: "", breed: "", age_years: "", weight_lbs: "",
    temperament: "playful", goals: "",
  });

  // Book session dialog
  const [bookOpen, setBookOpen] = useState(false);
  const [booking, setBooking] = useState({
    dog_id: "", type: "one_on_one", scheduled_at: "", location: "In-home",
    duration_min: 60, notes: "",
  });

  // Chippy AI
  const sessionIdRef = useRef(`chippy-${Math.random().toString(36).slice(2, 10)}`);
  const [aiHistory, setAiHistory] = useState([]);
  const [aiInput, setAiInput] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  const load = async () => {
    const [p, s, ta] = await Promise.all([
      api.get("/parents/me"),
      api.get("/sessions"),
      api.get("/tasks"),
    ]);
    setProfile(p.data);
    setSessions(s.data);
    setTasks(ta.data);
  };
  useEffect(() => { load(); }, []);

  // ----- Dog actions -----
  const submitNewDog = async () => {
    if (!newDog.name.trim()) {
      toast.error("Give your pup a name 🐶");
      return;
    }
    try {
      const payload = {
        ...newDog,
        age_years: newDog.age_years ? parseFloat(newDog.age_years) : null,
        weight_lbs: newDog.weight_lbs ? parseInt(newDog.weight_lbs) : null,
        goals: newDog.goals ? newDog.goals.split(",").map((s) => s.trim()).filter(Boolean) : [],
      };
      await api.post("/dogs", payload);
      toast.success(`${newDog.name} added!`);
      setAddDogOpen(false);
      setNewDog({ name: "", breed: "", age_years: "", weight_lbs: "", temperament: "playful", goals: "" });
      await load();
    } catch (e) {
      toast.error("Could not add dog");
    }
  };

  // ----- Booking -----
  const submitBooking = async () => {
    if (!booking.dog_id || !booking.scheduled_at) {
      toast.error("Pick a dog and a date/time");
      return;
    }
    const myTrainerId = (profile?.dogs || [])
      .map(() => null)
      .find(Boolean);
    // Use any seeded session's trainer_id for this parent if we have one
    let trainer_id = null;
    if (sessions.length > 0) trainer_id = sessions[0].trainer_id;
    if (!trainer_id && profile?.id) {
      // attempt to fetch trainer from leads
      try {
        const { data } = await api.get("/leads");
        const matchedLead = data.find((l) => l.assigned_trainer_id);
        if (matchedLead) trainer_id = matchedLead.assigned_trainer_id;
      } catch {}
    }
    if (!trainer_id) {
      toast.error("No trainer assigned yet — Delicia will reach out to set up your first session.");
      return;
    }
    try {
      await api.post("/sessions", {
        parent_id: profile.id,
        dog_id: booking.dog_id,
        trainer_id,
        scheduled_at: new Date(booking.scheduled_at).toISOString(),
        duration_min: parseInt(booking.duration_min),
        location: booking.location,
        type: booking.type,
      });
      toast.success("Session requested! Your trainer will confirm shortly.");
      setBookOpen(false);
      setBooking({ dog_id: "", type: "one_on_one", scheduled_at: "", location: "In-home", duration_min: 60, notes: "" });
      await load();
    } catch (e) {
      toast.error("Could not request session");
    }
  };

  // ----- Homework -----
  const toggleTask = async (task) => {
    const next = task.status === "done" ? "open" : "done";
    await api.patch(`/tasks/${task.id}`, { status: next });
    await load();
  };

  // ----- Chippy AI -----
  const askChippy = async (e) => {
    e?.preventDefault?.();
    if (!aiInput.trim() || aiBusy) return;
    const myMsg = aiInput.trim();
    setAiInput("");
    setAiBusy(true);
    setAiHistory((h) => [...h, { role: "user", text: myMsg }]);
    try {
      const { data } = await api.post("/ai/ask-trainer", {
        session_id: sessionIdRef.current,
        message: myMsg,
      });
      setAiHistory((h) => [...h, { role: "ai", text: data.reply }]);
    } catch (err) {
      toast.error("Chippy is taking a quick nap 😴 Try again in a moment.");
      setAiHistory((h) => [...h, { role: "ai", text: "Woof — I'm taking a quick nap. Try me again in a sec!" }]);
    } finally {
      setAiBusy(false);
    }
  };

  const dogs = profile?.dogs || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">{t("parent.title")}</h1>
          <p className="text-muted-foreground">
            Hi {user?.name?.split(" ")[0]} 👋 — {profile?.city}, {profile?.country}
          </p>
        </div>
        <Badge className="rounded-full bg-secondary text-secondary-foreground">
          <PawPrint className="w-3.5 h-3.5 mr-1.5" />Family
        </Badge>
      </div>

      <Tabs defaultValue="dogs">
        <TabsList className="rounded-full p-1 bg-muted overflow-x-auto flex-nowrap">
          <TabsTrigger value="dogs" className="rounded-full px-5" data-testid="parent-tab-dogs">{t("parent.tab.dogs")}</TabsTrigger>
          <TabsTrigger value="sessions" className="rounded-full px-5" data-testid="parent-tab-sessions">{t("parent.tab.sessions")}</TabsTrigger>
          <TabsTrigger value="homework" className="rounded-full px-5" data-testid="parent-tab-homework">{t("parent.tab.homework")}</TabsTrigger>
          <TabsTrigger value="ask" className="rounded-full px-5" data-testid="parent-tab-ask">Ask Chippy</TabsTrigger>
        </TabsList>

        {/* MY DOGS */}
        <TabsContent value="dogs" className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{dogs.length} pup{dogs.length === 1 ? "" : "s"} in your family</p>
            <Button onClick={() => setAddDogOpen(true)} className="rounded-full" data-testid="add-dog-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add a dog
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {dogs.length === 0 && (
              <Card className="rounded-3xl border-dashed border-2 border-border p-10 text-center sm:col-span-2 lg:col-span-3">
                <PawPrint className="w-10 h-10 text-primary mx-auto mb-2" />
                <p className="text-muted-foreground">No dogs yet. Click "Add a dog" to start.</p>
              </Card>
            )}
            {dogs.map((d) => (
              <button key={d.id} onClick={() => setDogDetail(d)} className="text-left" data-testid={`dog-${d.id}`}>
                <Card className="rounded-3xl border-border/60 soft-shadow lift overflow-hidden cursor-pointer h-full">
                  <img src={d.photo} alt={d.name} className="w-full aspect-[4/3] object-cover" />
                  <CardContent className="p-5">
                    <h3 className="font-display text-xl font-bold">{d.name}</h3>
                    <p className="text-xs text-muted-foreground">{d.breed} · {d.age_years}y · {d.weight_lbs}lb</p>
                    <Badge className="rounded-full bg-accent/30 text-accent-foreground mt-2">{d.temperament}</Badge>
                  </CardContent>
                </Card>
              </button>
            ))}
          </div>
        </TabsContent>

        {/* SESSIONS */}
        <TabsContent value="sessions" className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{sessions.length} session{sessions.length === 1 ? "" : "s"}</p>
            <Button onClick={() => setBookOpen(true)} className="rounded-full" data-testid="book-session-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Book a session
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {sessions.length === 0 && (
              <p className="text-muted-foreground sm:col-span-2 lg:col-span-3">No sessions yet — book your first one above.</p>
            )}
            {sessions.map((s) => (
              <Card key={s.id} className="rounded-3xl border-border/60 soft-shadow">
                <CardContent className="p-5">
                  <Badge className={`rounded-full mb-2 ${
                    s.status === "completed" ? "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300" :
                    s.status === "requested" ? "bg-accent/30 text-accent-foreground" :
                    "bg-primary/15 text-primary"
                  }`}>{s.status}</Badge>
                  <p className="font-bold flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-primary" />
                    {new Date(s.scheduled_at).toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{s.location} · {s.type?.replace("_", " ")}</p>
                  {s.summary && <p className="text-sm mt-3 text-muted-foreground line-clamp-3">{s.summary}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* HOMEWORK */}
        <TabsContent value="homework" className="mt-6">
          <Card className="rounded-3xl border-border/60 soft-shadow">
            <CardContent className="p-0 divide-y divide-border/60">
              {tasks.length === 0 ? (
                <div className="p-6 text-muted-foreground">No homework yet. Your trainer will assign tasks here.</div>
              ) : (
                tasks.map((tk) => (
                  <button
                    key={tk.id}
                    onClick={() => toggleTask(tk)}
                    className="w-full text-left p-4 hover:bg-muted/40 transition flex items-start gap-4"
                    data-testid={`task-${tk.id}`}
                  >
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mt-0.5 shrink-0 ${
                      tk.status === "done" ? "bg-primary border-primary" : "border-muted-foreground"
                    }`}>
                      {tk.status === "done" && <CheckCircle2 className="w-4 h-4 text-primary-foreground" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-bold ${tk.status === "done" ? "line-through text-muted-foreground" : ""}`}>
                        {tk.title}
                      </p>
                      {tk.description && (
                        <p className="text-sm text-muted-foreground mt-0.5">{tk.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                        {tk.due_at && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" /> Due {new Date(tk.due_at).toLocaleDateString()}
                          </span>
                        )}
                        {tk.created_by_name && <span>· From {tk.created_by_name}</span>}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ASK CHIPPY */}
        <TabsContent value="ask" className="mt-6">
          <Card className="rounded-3xl border-border/60 soft-shadow">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={CHIPPY_AVATAR}
                  alt="Chippy"
                  className="w-12 h-12 rounded-2xl object-cover bg-secondary/40"
                />
                <div>
                  <h3 className="font-display text-xl font-bold flex items-center gap-2">
                    Ask Chippy <Sparkles className="w-4 h-4 text-accent" />
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Your AI dog-training sidekick — friendly, calm, and family-first.
                  </p>
                </div>
              </div>
              <div className="bg-muted/40 rounded-2xl p-4 max-h-[45vh] overflow-y-auto space-y-3 mb-3">
                {aiHistory.length === 0 && (
                  <div className="flex items-start gap-2.5">
                    <img src={CHIPPY_AVATAR} alt="Chippy" className="w-8 h-8 rounded-xl shrink-0" />
                    <div className="bg-card border border-border/60 rounded-2xl p-3 text-sm">
                      {lang === "es"
                        ? "Hola, soy Chippy 🐾 Pregúntame lo que sea sobre tu perro — comportamiento, ansiedad, niños y perros, cachorros…"
                        : "Hi, I'm Chippy 🐾 Ask me anything about your dog — behavior, anxiety, kids and dogs, puppies…"}
                    </div>
                  </div>
                )}
                {aiHistory.map((m, i) => (
                  m.role === "user" ? (
                    <div key={i} className="ml-auto bg-primary text-primary-foreground rounded-2xl p-3 text-sm max-w-[80%] whitespace-pre-wrap">
                      {m.text}
                    </div>
                  ) : (
                    <div key={i} className="flex items-start gap-2.5 max-w-[90%]">
                      <img src={CHIPPY_AVATAR} alt="Chippy" className="w-8 h-8 rounded-xl shrink-0" />
                      <div className="bg-card border border-border/60 rounded-2xl p-3 text-sm whitespace-pre-wrap">{m.text}</div>
                    </div>
                  )
                ))}
                {aiBusy && (
                  <div className="flex items-start gap-2.5">
                    <img src={CHIPPY_AVATAR} alt="Chippy" className="w-8 h-8 rounded-xl shrink-0" />
                    <div className="bg-card border border-border/60 rounded-2xl p-3 text-sm text-muted-foreground italic">
                      Chippy is thinking…
                    </div>
                  </div>
                )}
              </div>
              <form onSubmit={askChippy} className="flex gap-2" data-testid="chippy-form">
                <Input
                  value={aiInput}
                  onChange={(e) => setAiInput(e.target.value)}
                  placeholder={lang === "es" ? "Mi perro ladra al timbre…" : "My dog barks at the doorbell…"}
                  className="rounded-full"
                  data-testid="chippy-input"
                />
                <Button type="submit" disabled={aiBusy} className="rounded-full" data-testid="chippy-send">
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* DOG PROFILE DIALOG */}
      <Dialog open={!!dogDetail} onOpenChange={(v) => !v && setDogDetail(null)}>
        <DialogContent className="rounded-3xl max-w-xl p-0 overflow-hidden" data-testid="dog-detail-modal">
          {dogDetail && (
            <>
              <img src={dogDetail.photo} alt={dogDetail.name} className="w-full aspect-[16/9] object-cover" />
              <div className="p-6">
                <DialogHeader>
                  <DialogTitle className="font-display text-3xl font-black">{dogDetail.name}</DialogTitle>
                  <DialogDescription>
                    {dogDetail.breed} · {dogDetail.age_years}y · {dogDetail.weight_lbs}lb
                  </DialogDescription>
                </DialogHeader>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Badge className="rounded-full bg-accent/30 text-accent-foreground">{dogDetail.temperament}</Badge>
                  {(dogDetail.goals || []).map((g) => (
                    <Badge key={g} variant="outline" className="rounded-full">{g}</Badge>
                  ))}
                </div>
                <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-xl bg-primary/10 p-3">
                    <p className="text-xs uppercase tracking-wider text-primary font-bold">Sessions</p>
                    <p className="text-xl font-black mt-0.5">{sessions.filter((s) => s.dog_id === dogDetail.id).length}</p>
                  </div>
                  <div className="rounded-xl bg-secondary/40 p-3">
                    <p className="text-xs uppercase tracking-wider font-bold">Homework</p>
                    <p className="text-xl font-black mt-0.5">{tasks.length}</p>
                  </div>
                  <div className="rounded-xl bg-accent/30 p-3">
                    <p className="text-xs uppercase tracking-wider font-bold">Streak</p>
                    <p className="text-xl font-black mt-0.5">🔥 7d</p>
                  </div>
                </div>
                <Button
                  className="rounded-full font-bold w-full mt-5"
                  onClick={() => {
                    setBooking((b) => ({ ...b, dog_id: dogDetail.id }));
                    setDogDetail(null);
                    setBookOpen(true);
                  }}
                >
                  Book a session for {dogDetail.name}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ADD DOG DIALOG */}
      <Dialog open={addDogOpen} onOpenChange={setAddDogOpen}>
        <DialogContent className="rounded-3xl max-w-lg" data-testid="add-dog-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Add a dog 🐾</DialogTitle>
            <DialogDescription>We'll keep your family roster up to date.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={newDog.name} onChange={(e) => setNewDog({ ...newDog, name: e.target.value })} className="rounded-xl mt-1" data-testid="new-dog-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Breed</Label>
                <Input value={newDog.breed} onChange={(e) => setNewDog({ ...newDog, breed: e.target.value })} className="rounded-xl mt-1" />
              </div>
              <div>
                <Label>Age (years)</Label>
                <Input type="number" step="0.5" value={newDog.age_years} onChange={(e) => setNewDog({ ...newDog, age_years: e.target.value })} className="rounded-xl mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Weight (lb)</Label>
                <Input type="number" value={newDog.weight_lbs} onChange={(e) => setNewDog({ ...newDog, weight_lbs: e.target.value })} className="rounded-xl mt-1" />
              </div>
              <div>
                <Label>Temperament</Label>
                <Select value={newDog.temperament} onValueChange={(v) => setNewDog({ ...newDog, temperament: v })}>
                  <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TEMPERAMENTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Goals (comma-separated)</Label>
              <Input value={newDog.goals} onChange={(e) => setNewDog({ ...newDog, goals: e.target.value })} placeholder="leash, recall, calm at home" className="rounded-xl mt-1" />
            </div>
            <Button onClick={submitNewDog} className="w-full rounded-full font-bold mt-2" data-testid="new-dog-submit">
              Add to my family
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* BOOK SESSION DIALOG */}
      <Dialog open={bookOpen} onOpenChange={setBookOpen}>
        <DialogContent className="rounded-3xl max-w-lg" data-testid="book-session-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Book a session 📅</DialogTitle>
            <DialogDescription>Your trainer will confirm shortly.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Which dog?</Label>
              <Select value={booking.dog_id} onValueChange={(v) => setBooking({ ...booking, dog_id: v })}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="booking-dog"><SelectValue placeholder="Select a dog" /></SelectTrigger>
                <SelectContent>
                  {dogs.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Service</Label>
              <Select value={booking.type} onValueChange={(v) => setBooking({ ...booking, type: v })}>
                <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SERVICE_OPTS.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>When</Label>
                <Input type="datetime-local" value={booking.scheduled_at} onChange={(e) => setBooking({ ...booking, scheduled_at: e.target.value })} className="rounded-xl mt-1" data-testid="booking-time" />
              </div>
              <div>
                <Label>Duration (min)</Label>
                <Select value={String(booking.duration_min)} onValueChange={(v) => setBooking({ ...booking, duration_min: v })}>
                  <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[45, 60, 90, 120].map((m) => <SelectItem key={m} value={String(m)}>{m} min</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Where</Label>
              <Input value={booking.location} onChange={(e) => setBooking({ ...booking, location: e.target.value })} className="rounded-xl mt-1" placeholder="In-home, Virtual, Park…" />
            </div>
            <Button onClick={submitBooking} className="w-full rounded-full font-bold mt-2" data-testid="booking-submit">
              Request session
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              <MapPin className="w-3 h-3 inline mr-1" />
              We serve all areas around major hub cities — closest hub: {profile?.city}.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
